import { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Grid,
  Paper,
  Chip,
  TextField,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Slider
} from '@mui/material';
import { 
  Star, StarBorder, ContentCopy, GridView, BarChart, 
  Search, AccessTime, ChatBubbleOutline, Terminal, FileDownload,
  FilterList, Layers
} from '@mui/icons-material';
import { SessionContext } from '../../context/SessionContext';
import { getMessagesBySession } from '../../service/chatService';
import { toggleStarUser } from '../../service/userService';
import { getInteractionsBySession } from '../../service/interactionService';
import { getSubmissionsBySessionId } from '../../service/submissionService';
import { getAllCodeByUserId } from '../../service/codeService';
import { getRrwebEventsBySessionAndUser } from '../../service/rrwebService';
import { getTelemetryEventsBySession } from '../workspace/service/telemetryService';
import SessionAnalytics from './SessionAnalytics';

const StudentGridView = ({ students, onStudentClick, onStudentsUpdate, loadEventAdapter }) => {
  const { session } = useContext(SessionContext);
  const [enrichedStudents, setEnrichedStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  
  // -- FILTER STATES --
  const [searchQuery, setSearchQuery] = useState('');
  const [filterChats, setFilterChats] = useState(false);
  const [filterCode, setFilterCode] = useState(false);
  const [showOnlyStarred, setShowOnlyStarred] = useState(false);
  const [afterTimestamp, setAfterTimestamp] = useState('');
  
  // Copy/Export State
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  
  // Tab & Analytics State
  const [activeTab, setActiveTab] = useState(0);
  const [analyticsExportData, setAnalyticsExportData] = useState(null);

  // -- EXPORT DIALOG STATE --
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    interactions: true, // Clickstream
    messages: true,     // Chat
    code: true,         // Code snapshots
    tests: true,        // Test runs
    terminal: true,     // RRWeb/Terminal data
    
    // Global detail filters
    includeKeystrokes: false, // Applies to Messages, Code, AND RRWeb
    includeMouseEvents: false, // Applies to RRWeb
    
    analytics: false,    // Summary stats
    
    // Batching
    batchCount: 1       // Default to single file
  });

  // Enrich students with chat/code activity - sourced from telemetry_events, not the
  // message/submission tables this used to read (dead since 2026-08-21/22, see
  // RawDataPanel.jsx/SessionAnalytics.jsx for the same fix). Every card in this grid
  // showed 0 messages/0 activity for any current session before this.
  useEffect(() => {
    // A run for an older `students` list (e.g. the empty one before the list loads) can
    // finish after the current one; it must not overwrite the current result.
    let cancelled = false;
    const enrichStudentData = async () => {
      if (!session?.id) return;

      try {
        const eventsByUser = await getTelemetryEventsBySession(session.id);
        if (cancelled) return;
        // Pages with their own event types (Reasoning Gap) supply an adapter, see RawDataPanel.jsx.
        const adapt = loadEventAdapter ? await loadEventAdapter().catch(() => null) : null;

        const enriched = students.map(student => {
          const rawEvents = eventsByUser[student.id] || eventsByUser[String(student.id)] || [];
          const events = adapt ? adapt(rawEvents, student.id) : rawEvents;
          const studentMessages = events.filter(e => e.type === 'CHAT_QUERY');
          const studentSubmissions = events.filter(e => e.type === 'TERMINAL_RUN');

          const messageTimes = studentMessages.map(e => e.timestamp);
          const submissionTimes = studentSubmissions.map(e => e.timestamp);
          const allTimes = [...messageTimes, ...submissionTimes];

          const lastActiveTime = allTimes.length > 0
            ? new Date(Math.max(...allTimes))
            : null;

          return {
            ...student,
            messageCount: studentMessages.length,
            submissionCount: studentSubmissions.length,
            lastActiveTime: lastActiveTime,
            hasMessages: studentMessages.length > 0,
            hasCodeActivity: studentSubmissions.length > 0
          };
        });

        if (cancelled) return;
        setEnrichedStudents(enriched);
        setFilteredStudents(enriched);
      } catch (error) {
        console.error('Error enriching students:', error);
        if (cancelled) return;
        setEnrichedStudents(students);
        setFilteredStudents(students);
      }
    };

    enrichStudentData();
    return () => { cancelled = true; };
  }, [students, session?.id, loadEventAdapter]);

  // Apply filters
  useEffect(() => {
    let filtered = [...enrichedStudents];
    
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        (s.first_name + ' ' + s.last_name).toLowerCase().includes(lowerQuery) ||
        (s.email || '').toLowerCase().includes(lowerQuery)
      );
    }

    if (filterChats) filtered = filtered.filter(s => s.hasMessages);
    if (filterCode) filtered = filtered.filter(s => s.hasCodeActivity);
    if (showOnlyStarred) filtered = filtered.filter(s => s.is_starred);
    
    if (afterTimestamp) {
      const timestampDate = new Date(afterTimestamp);
      filtered = filtered.filter(s => 
        s.lastActiveTime && s.lastActiveTime >= timestampDate
      );
    }
    
    setFilteredStudents(filtered);
  }, [enrichedStudents, searchQuery, filterChats, filterCode, showOnlyStarred, afterTimestamp]);

  const handleToggleStar = async (e, student) => {
    e.stopPropagation();
    try {
      const newStarredState = await toggleStarUser(student.id);
      setEnrichedStudents(prev => prev.map(s => 
        s.id === student.id ? { ...s, is_starred: newStarredState } : s
      ));
      if (onStudentsUpdate) onStudentsUpdate();
    } catch (error) {
      console.error('Failed to toggle star:', error);
    }
  };

  const handleCopyAnalytics = async () => {
    if (!analyticsExportData) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(analyticsExportData, null, 2));
      setCopyMessage('Analytics JSON copied to clipboard!');
      setCopySuccess(true);
    } catch (error) {
      console.error('Failed to copy analytics', error);
      alert('Failed to copy analytics data');
    }
  };

  const handleExportOptionChange = (event) => {
    const { name, checked, value, type } = event.target;
    setExportOptions({
      ...exportOptions,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleCustomExport = async () => {
    if (filteredStudents.length === 0) {
      setCopyMessage("No students match your filters to export.");
      setCopySuccess(true);
      return;
    }

    setCopyLoading(true);
    setExportDialogOpen(false); 

    try {
      // 1. Fetch Base Data
      let allInteractions = [], allTestRuns = [], allMessages = [];
      const promises = [];

      if (exportOptions.interactions) promises.push(getInteractionsBySession(session.id));
      else promises.push(Promise.resolve([]));

      if (exportOptions.tests) promises.push(getSubmissionsBySessionId(session.id));
      else promises.push(Promise.resolve([]));

      if (exportOptions.messages) promises.push(getMessagesBySession(session.id));
      else promises.push(Promise.resolve([]));

      [allInteractions, allTestRuns, allMessages] = await Promise.all(promises);

      // 2. Process ALL filtered students first
      const studentsToExport = filteredStudents; 
      
      const allProcessedStudents = await Promise.all(
        studentsToExport.map(async (student) => {
          const studentTimelineEvents = [];

          // Helper to strip keystrokes
          const processObject = (obj) => {
            if (exportOptions.includeKeystrokes) return obj;
            const clean = { ...obj };
            if (clean.keystrokes) delete clean.keystrokes;
            return clean;
          };

          // A. Messages
          if (exportOptions.messages) {
            const msgs = allMessages.filter(m => 
              m.sender_id === student.id || m.recipient_id === student.id
            );
            studentTimelineEvents.push(...msgs.map(m => processObject({ 
              type: 'message', 
              timestamp: new Date(m.created_time || m.timestamp), 
              ...m 
            })));
          }

          // B. Interactions
          if (exportOptions.interactions) {
            const ints = allInteractions.filter(i => i.user_id === student.id);
            studentTimelineEvents.push(...ints.map(i => processObject({ 
              type: 'interaction', 
              timestamp: new Date(i.created_time), 
              ...i 
            })));
          }

          // C. Tests
          if (exportOptions.tests) {
            const tests = allTestRuns.filter(s => s.user_id === student.id);
            studentTimelineEvents.push(...tests.map(s => processObject({ 
              type: 'test_run', 
              timestamp: new Date(s.created_time), 
              ...s 
            })));
          }

          // D. Code 
          if (exportOptions.code) {
            try {
              const codeData = await getAllCodeByUserId(student.id);
              const studentCode = Array.isArray(codeData) ? codeData : [codeData].filter(Boolean);
              studentTimelineEvents.push(...studentCode.map(c => processObject({ 
                type: 'code_submission', 
                timestamp: new Date(c.created_time), 
                ...c 
              })));
            } catch (err) {
              console.error(`Error fetching code for ${student.id}`, err);
            }
          }

          // E. Terminal/RRWeb
          if (exportOptions.terminal) {
            try {
              const rrwebBatches = await getRrwebEventsBySessionAndUser(session.id, student.id);
              if (rrwebBatches && Array.isArray(rrwebBatches)) {
                
                // 1. Terminal Output
                const terminalOutput = rrwebBatches
                  .filter(e => e.type === 3 && e.data?.source === 0 && e.data?.texts)
                  .flatMap(e => 
                    e.data.texts
                      .filter(t => t.value && t.value.trim().length > 0)
                      .map(t => ({
                        type: 'terminal_output',
                        timestamp: new Date(e.timestamp),
                        node_id: t.id,
                        text: t.value
                      }))
                  )
                  .filter(t => {
                    const text = t.text.trim();
                    const isError = text.includes('Error') || text.includes('Traceback');
                    const isNoise = text === 'Run Tests' || text === 'Running...' || text.length < 5;
                    return isError && !isNoise;
                  });
                
                studentTimelineEvents.push(...terminalOutput);

                // 2. Raw RRWeb Events
                if (exportOptions.includeMouseEvents || exportOptions.includeKeystrokes) {
                   const rawEvents = rrwebBatches.map(e => ({
                     type: 'rrweb_raw_event',
                     timestamp: new Date(e.timestamp),
                     data: e
                   }));

                   const filteredRawEvents = rawEvents.filter(evt => {
                     const rrwebType = evt.data.type;
                     const rrwebSource = evt.data.data?.source;

                     if (rrwebType !== 3) return true;
                     if (rrwebSource === 1) return exportOptions.includeMouseEvents;
                     if (rrwebSource === 2) return exportOptions.includeMouseEvents;
                     if (rrwebSource === 5) return exportOptions.includeKeystrokes;

                     return true;
                   });
                   studentTimelineEvents.push(...filteredRawEvents);
                }
              }
            } catch (err) {
              console.error(`Error fetching rrweb for ${student.id}`, err);
            }
          }

          studentTimelineEvents.sort((a, b) => a.timestamp - b.timestamp);

          return {
            student: { 
              id: student.id, 
              email: student.email, 
              name: `${student.first_name} ${student.last_name}` 
            },
            summary: { 
              total_events: studentTimelineEvents.length 
            },
            chronological_timeline: studentTimelineEvents
          };
        })
      );

      // 3. Batching and Download
      const totalStudents = allProcessedStudents.length;
      const batches = Math.max(1, parseInt(exportOptions.batchCount) || 1);
      const chunkSize = Math.ceil(totalStudents / batches);

      for (let i = 0; i < batches; i++) {
        const start = i * chunkSize;
        const end = start + chunkSize;
        const batchData = allProcessedStudents.slice(start, end);

        if (batchData.length === 0) continue;

        const exportPayload = {
          session_id: session.id,
          export_timestamp: new Date().toISOString(),
          batch_index: i + 1,
          total_batches: batches,
          students: batchData,
          filters_applied: {
            searchQuery,
            filterChats,
            filterCode,
            showOnlyStarred,
            afterTimestamp
          }
        };

        if (exportOptions.analytics && analyticsExportData && i === 0) {
           // Only attach analytics to the first batch to avoid redundancy
           exportPayload.analytics = analyticsExportData;
        }

        const jsonString = JSON.stringify(exportPayload, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        link.href = url;
        // Naming: Session_123_Export_Batch_1_of_4.json
        link.download = `Session_${session.id}_Export_Batch_${i + 1}_of_${batches}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Small delay to ensure browser handles multiple downloads
        await new Promise(r => setTimeout(r, 500));
      }

      setCopyMessage(`Successfully exported ${totalStudents} students in ${batches} batch(es)!`);
      setCopySuccess(true);

    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Export failed. Check console for details.');
    } finally {
      setCopyLoading(false);
    }
  };

  const getStudentColor = (index) => {
    const colors = ['#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2'];
    return colors[index % colors.length];
  };

  const getActiveFilterNames = () => {
    const active = [];
    if (searchQuery) active.push(`Search: "${searchQuery}"`);
    if (filterChats) active.push("Has Chats");
    if (filterCode) active.push("Has Code");
    if (showOnlyStarred) active.push("Starred Only");
    if (afterTimestamp) active.push("Time Filter");
    return active;
  };

  return (
    <Box sx={{ minHeight: '600px' }}>
      {/* Header Area */}
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider', 
        mb: 3, 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          sx={{ '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', fontSize: '1rem', minHeight: 56 } }}
        >
          <Tab icon={<GridView sx={{ mr: 1 }} />} iconPosition="start" label="View Students" />
          <Tab icon={<BarChart sx={{ mr: 1 }} />} iconPosition="start" label="Analytics" />
        </Tabs>

        <Box sx={{ display: 'flex', gap: 1, pb: 1, pr: 1 }}>
          {activeTab === 0 ? (
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={<FileDownload />}
                onClick={() => setExportDialogOpen(true)}
                disabled={copyLoading || filteredStudents.length === 0}
              >
                {copyLoading ? 'Exporting...' : 'Export Data...'}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<ContentCopy />}
              onClick={handleCopyAnalytics}
              disabled={!analyticsExportData}
            >
              Copy Analytics JSON
            </Button>
          )}
        </Box>
      </Box>

      {/* Tab 0: Student Grid with Search & Filter */}
      {activeTab === 0 && (
        <>
          <Paper 
            elevation={0} 
            sx={{ 
              mb: 3, p: 2, bgcolor: '#f8f9fa', 
              border: '1px solid #e0e0e0', borderRadius: 2,
              display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'
            }}
          >
            <TextField
              size="small"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flex: 1, minWidth: '200px', bgcolor: 'white' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ width: 1, height: 24, bgcolor: '#bdbdbd', display: { xs: 'none', md: 'block' } }} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={<ChatBubbleOutline />} label="Has Chats"
                color={filterChats ? "primary" : "default"} variant={filterChats ? "filled" : "outlined"}
                onClick={() => setFilterChats(!filterChats)} clickable
              />
              <Chip
                icon={<Terminal />} label="Has Code/Tests"
                color={filterCode ? "secondary" : "default"} variant={filterCode ? "filled" : "outlined"}
                onClick={() => setFilterCode(!filterCode)} clickable
              />
              <Chip
                icon={showOnlyStarred ? <Star /> : <StarBorder />} label="Starred"
                color={showOnlyStarred ? "warning" : "default"} variant={showOnlyStarred ? "filled" : "outlined"}
                onClick={() => setShowOnlyStarred(!showOnlyStarred)} clickable
              />
            </Box>
            <TextField
              size="small" type="datetime-local"
              value={afterTimestamp} onChange={(e) => setAfterTimestamp(e.target.value)}
              sx={{ bgcolor: 'white' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><AccessTime fontSize="small" /></InputAdornment>
                ),
              }}
            />
          </Paper>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'right' }}>
            Showing {filteredStudents.length} of {students.length} students
          </Typography>
          
          {filteredStudents.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary', bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Search sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
              <Typography>No students match your filters.</Typography>
              <Button sx={{ mt: 1 }} onClick={() => { setSearchQuery(''); setFilterChats(false); setFilterCode(false); setShowOnlyStarred(false); }}>
                Clear All Filters
              </Button>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {filteredStudents.map((student, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={student.id}>
                  <Paper
                    elevation={2}
                    sx={{ 
                      p: 3, textAlign: 'center', cursor: 'pointer', 
                      border: student.is_starred ? '2px solid #ffd700' : '1px solid transparent',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: 4, borderColor: '#1976d2' }
                    }}
                    onClick={() => onStudentClick(student)}
                  >
                    <Box sx={{ position: 'relative' }}>
                        <IconButton 
                            size="small" sx={{ position: 'absolute', top: -10, left: -10 }}
                            onClick={(e) => handleToggleStar(e, student)}
                        >
                            {student.is_starred ? <Star sx={{ color: '#ffd700' }} /> : <StarBorder />}
                        </IconButton>
                        
                        <Box sx={{ position: 'absolute', top: -10, right: -10, display: 'flex', gap: 0.5 }}>
                          {student.hasMessages && (
                            <Tooltip title={`${student.messageCount} chat messages`}>
                              <Box sx={{ 
                                bgcolor: '#4caf50', color: '#fff', borderRadius: 4, px: 0.8, py: 0.2,
                                fontSize: '0.7rem', fontWeight: 'bold', display: 'flex', alignItems: 'center'
                              }}>
                                <ChatBubbleOutline sx={{ fontSize: '0.8rem', mr: 0.5 }} />
                                {student.messageCount}
                              </Box>
                            </Tooltip>
                          )}
                          {student.hasCodeActivity && (
                            <Tooltip title={`${student.submissionCount} code executions`}>
                              <Box sx={{ 
                                bgcolor: '#9c27b0', color: '#fff', borderRadius: 4, px: 0.8, py: 0.2,
                                fontSize: '0.7rem', fontWeight: 'bold', display: 'flex', alignItems: 'center'
                              }}>
                                <Terminal sx={{ fontSize: '0.8rem', mr: 0.5 }} />
                                {student.submissionCount}
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                        
                        <Avatar sx={{ width: 80, height: 80, margin: '0 auto 16px', bgcolor: getStudentColor(index), fontSize: '2rem' }}>
                           {student.first_name?.[0]}{student.last_name?.[0]}
                        </Avatar>
                        
                        <Typography variant="h6" noWrap title={`${student.first_name} ${student.last_name}`}>
                          {student.first_name} {student.last_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap title={student.email}>
                          {student.email}
                        </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {activeTab === 1 && (
        <SessionAnalytics 
          students={students} 
          onAnalyticsReady={setAnalyticsExportData} 
        />
      )}

      {/* --- EXPORT CONFIGURATION DIALOG --- */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Export Configuration</DialogTitle>
        <DialogContent sx={{ minWidth: '400px' }}>
          
          <Alert severity="info" sx={{ mb: 2 }} icon={<FilterList fontSize="inherit" />}>
            <Typography variant="subtitle2" fontWeight={600}>
              Exporting {filteredStudents.length} Students
            </Typography>
            {getActiveFilterNames().length > 0 ? (
                <Typography variant="caption" display="block">
                  Matches: {getActiveFilterNames().join(', ')}
                </Typography>
            ) : (
                <Typography variant="caption">All students included</Typography>
            )}
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the data points to include in the JSON export.
          </Typography>
          <FormControl component="fieldset" variant="standard" fullWidth>
            
            {/* 1. DATA SELECTION */}
            <FormLabel component="legend">Timeline Data</FormLabel>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={exportOptions.messages} onChange={handleExportOptionChange} name="messages" />}
                label="Chat Messages"
              />
              <FormControlLabel
                control={<Checkbox checked={exportOptions.interactions} onChange={handleExportOptionChange} name="interactions" />}
                label="UI Interactions (Clicks)"
              />
              <FormControlLabel
                control={<Checkbox checked={exportOptions.code} onChange={handleExportOptionChange} name="code" />}
                label="Code Snapshots"
              />
              <FormControlLabel
                control={<Checkbox checked={exportOptions.tests} onChange={handleExportOptionChange} name="tests" />}
                label="Test Runs"
              />
              <FormControlLabel
                control={<Checkbox checked={exportOptions.terminal} onChange={handleExportOptionChange} name="terminal" />}
                label="RRWeb / Terminal Output"
              />
            </FormGroup>
            
            {/* 2. DETAIL FILTERS */}
            <Box sx={{ mt: 2 }}>
              <FormLabel component="legend">Detail Level</FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={exportOptions.includeKeystrokes} onChange={handleExportOptionChange} name="includeKeystrokes" size="small" />}
                  label={<Typography variant="body2">Include Keystroke Data (Messages & Inputs)</Typography>}
                />
                <FormControlLabel
                  control={<Checkbox checked={exportOptions.includeMouseEvents} onChange={handleExportOptionChange} name="includeMouseEvents" size="small" />}
                  label={<Typography variant="body2">Include Mouse Movements (RRWeb)</Typography>}
                />
              </FormGroup>
            </Box>
            
            {/* 3. BATCHING SETTINGS */}
            <Box sx={{ mt: 2 }}>
                <FormLabel component="legend">Batching</FormLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Layers sx={{ color: 'action.active', mr: 2 }} />
                    <TextField
                        name="batchCount"
                        label="Split into N files"
                        type="number"
                        size="small"
                        value={exportOptions.batchCount}
                        onChange={handleExportOptionChange}
                        inputProps={{ min: 1, max: filteredStudents.length }}
                        helperText={`Approx ${Math.ceil(filteredStudents.length / Math.max(1, exportOptions.batchCount))} students per file`}
                        fullWidth
                    />
                </Box>
            </Box>

            {/* 4. METADATA */}
            <Box sx={{ mt: 2 }}>
              <FormLabel component="legend">Metadata</FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={exportOptions.analytics} onChange={handleExportOptionChange} name="analytics" disabled={!analyticsExportData} />}
                  label={analyticsExportData ? "Include Session Analytics" : "Include Session Analytics (Load tab first)"}
                />
              </FormGroup>
            </Box>

          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCustomExport} variant="contained" disabled={copyLoading}>
            {copyLoading ? 'Processing...' : `Export ${filteredStudents.length} Students`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCopySuccess(false)} severity="success" sx={{ width: '100%' }}>
          {copyMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentGridView;